import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import boardToast, { TOAST_TYPE } from '../utils/boardToast';
import { buildEvents, formatOptions, selectLoader, sortMilestones } from 'utils/Helpers';
import { PLUS_ICON } from '../assets/image';
import html2canvas from 'html2canvas';
import ChannelWrapper from '../Milestone/ChannelWrapper';
import { fetchProjectBoard, fetchProjectMilestonesByBoard } from '../utils/fetchData';
import ProjectBoardHeading from '../ProjectBoardHeading/ProjectBoardHeading';
import MilestoneModal from '../Milestone/MilestoneModal';
import style from './BoardWrapper.module.css';
import Axios from '../utils/Axios';
import DeleteModal from 'components/DeleteModal';
import TaskCalendar from 'pages/projects/TaskCalendar';
import UpdatedOverview from 'pages/projects/UpdatedOverview';
import { fetchEnumOptions } from 'utils/fetchData';
import { useAuth } from 'contexts/AuthContext';
import APIServices from 'services/APIServices';
import ConfirmModal from 'components/ConfirmModal';
import AddStatusModal from 'components/AddStatusModal';
import InstructionsModal from 'components/InstructionsModal';
import 'assets/css/overview-updated.css';
import { disconnectGoogleCalendar, exportEventsToGoogleCalendar, prepareGoogleCalendarExport } from 'utils/googleCalendar';

const mergeMilestoneUpdate = (existingMilestone, incomingMilestone) => {
  if (existingMilestone?._id !== incomingMilestone?._id) return existingMilestone;

  const previousSubtasks = new Map(
    (existingMilestone?.subtask || []).map((subtask) => [subtask?._id, subtask])
  );

  const mergedSubtasks = Array.isArray(incomingMilestone?.subtask)
    ? incomingMilestone.subtask.map((incomingSubtask) => {
        const previousSubtask = previousSubtasks.get(incomingSubtask?._id);
        const previousStatus = previousSubtask?.status;
        const incomingStatus = incomingSubtask?.status;
        let status = incomingStatus;

        if (typeof incomingStatus === 'string') {
          status = previousStatus?._id === incomingStatus ? previousStatus : { _id: incomingStatus };
        } else if (incomingStatus && typeof incomingStatus === 'object') {
          status = {
            ...(previousStatus && typeof previousStatus === 'object' ? previousStatus : {}),
            ...incomingStatus
          };
        } else if (previousStatus !== undefined) {
          status = previousStatus;
        }

        return {
          ...previousSubtask,
          ...incomingSubtask,
          status
        };
      })
    : existingMilestone?.subtask;

  return {
    ...existingMilestone,
    ...incomingMilestone,
    subtask: mergedSubtasks
  };
};

const mergeMilestoneList = (existingMilestones = [], incomingMilestones = []) => {
  const existingById = new Map(existingMilestones.map((milestone) => [milestone?._id, milestone]));

  return incomingMilestones.map((incomingMilestone) =>
    mergeMilestoneUpdate(existingById.get(incomingMilestone?._id) || { _id: incomingMilestone?._id }, incomingMilestone)
  );
};

export default function BoardWrapper({
  projectBoardId,
  setBoards,
  channels,
  projectDetail,
  isTemplateActive,
  fetchTemplates,
  activeTemplateId,
  parentBoardId,
  fetchProjectBoards,
  isGeneralPreview = false,
  archiveBoardId,
  isArchivedActive,
  setArchivedActive,
  setArchiveBoardId,
  fetchArchivedBoards,
  setBoardTitle,
  mainProjectBoardId,
  isMasterCalender = false,
  masterMilestones = null,
  masterCalenderSocket,
  projectBoardIds,
  toggleModal = () => {},
  toggleBoardList = () => {},
  isBoardListCollapsed,
  companyTeam = [],
  tabsAtTopLevel = false,
  boardViewType: parentBoardViewType = 'board',
  setBoardViewType: setParentBoardViewType = () => {},
  isArchivedParent = false,
  onTemplateRenamed = () => {},
  onTemplateDeleted = () => {},
  onUseTemplate = null
}) {
  const showArchivedCardsRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const socket = useSocket();
  const milestonesRef = useRef([]);
  const parentRef = useRef();
  const boardRef = useRef(null);
  const { user, fetchUserInfo } = useAuth();
  const notificationData = location.state?.notificationData || null;
  const { projectId, milestoneId: milestoneIdFromParams, subtaskId: subtaskIdFromParams } = useParams();
  const [milestones, setMilestones] = useState([]);
  const [title, setTitle] = useState('');
  const [teams, setTeams] = useState([]);
  const [filteredMilestones, setFilteredMilestones] = useState([]);
  const [progressBarList, setProgressBarList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMilestonesLoading, setIsMilestonesLoading] = useState(false);
  const [isInviting, setInviting] = useState(false);
  const [showMilestoneSidebar, setShowMilestoneSidebar] = useState(null);
  const [userPermission, setUserPermission] = useState('View');
  const [projectBoardCreatedBy, setProjectBoardCreatedBy] = useState('');
  const [isProjectRelatedBoard, setIsProjectRelatedBoard] = useState(false);
  const [isSubtaskOpen, setSubtaskOpen] = useState(false);
  const [selectedSubtaskId, setSelectedSubtaskId] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isSubtaskDeleting, setSubtaskDeleting] = useState(null);
  const [isDeleting, setDeleting] = useState(false);
  const [calenderViewType, setCalenderViewType] = useState('dayGridMonth');
  const [internalBoardViewType, setInternalBoardViewType] = useState('board');
  const boardViewType = tabsAtTopLevel ? parentBoardViewType : internalBoardViewType;
  const setBoardViewType = tabsAtTopLevel ? setParentBoardViewType : setInternalBoardViewType;
  const [calendarTitle, setCalendarTitle] = useState('');
  const [showChecklist, setChecklist] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date()); // <-- new state
  const [openSubtasks, setOpenSubtasks] = useState({});
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [channelStatusOptions, setChannelStatusOptions] = useState([]);
  const [dueDateOptions, setDueDateOptions] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [isSaving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [parentMilestoneId, setParentMilestoneId] = useState('');
  const [statusOptions, setStatusOptions] = useState([]);
  const [defaultLabels, setDefaultLabels] = useState([]);
  const [reminderOptions, setReminderOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [addStatusOpen, setAddStatusOpen] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [showArchivedCards, setshowArchivedCards] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [filteredCalendarEvents, setFilteredCalendarEvents] = useState([]);
  const [isGoogleExporting, setGoogleExporting] = useState(false);
  const [isGoogleDisconnecting, setGoogleDisconnecting] = useState(false);
  //general calender functionalities:
  useEffect(() => {
    if (isMasterCalender) {
      setBoardViewType('calendar');
      // setCalenderViewType('timeGridDay');
      if (masterMilestones?.length > 0) {
        setMilestones(masterMilestones);
        setFilteredMilestones(masterMilestones);
      } else {
        setMilestones([]);
      }
    }
    if (companyTeam.length > 0) {
      setTeams(companyTeam);
    }
  }, [masterMilestones]);

  useEffect(() => {
    const events = buildEvents(milestones);
    setCalendarEvents(events);
    setFilteredCalendarEvents(events);
  }, [milestones]);

  useEffect(() => {
    if (!isMasterCalender) return;

    prepareGoogleCalendarExport().catch((error) => {
      console.error('Unable to prepare Google Calendar export:', error);
    });
  }, [isMasterCalender]);

  useEffect(() => {
    if (!isMasterCalender) {
      if (notificationData) {
        setShowMilestoneSidebar(notificationData.milestoneId._id);

        // Clear the notificationData from the state after using it
        navigate(location.pathname, { replace: true });
      } else if (subtaskIdFromParams && milestoneIdFromParams) {
        setShowMilestoneSidebar(milestoneIdFromParams);
        setSubtaskOpen(true);
        setSelectedSubtaskId(subtaskIdFromParams);
        navigate(`/project-board/${projectId}`, { replace: true });
      } else if (milestoneIdFromParams) {
        setShowMilestoneSidebar(milestoneIdFromParams);
        // Remove milestoneId from URL after using it
        navigate(`/project-board/${projectId}`, { replace: true });
      }
    }
  }, [notificationData, milestoneIdFromParams]);

  useEffect(() => {
    if (progressBarList && progressBarList.length > 0) {
      const formattedOptions = formatOptions(progressBarList);
      setChannelOptions(formattedOptions);
    }
  }, [progressBarList]);

  const formatTeamData = (teams) => {
    return teams.map((value) => ({
      ...value.userId,
      permission: value.permission?.name || 'View'
    }));
  };

  const loadEnumOptions = useCallback(async (key, setter) => {
    try {
      const options = await fetchEnumOptions(key);

      if (!Array.isArray(options) || options.length === 0) {
        setter([]);
        return;
      }

      setter(formatOptions(options));
    } catch (err) {
      console.error(`Failed to load enum: ${key}`, err);
      setter([]);
    }
  }, []);

  useEffect(() => {
    Promise.all([
      loadEnumOptions('milestonpriority', setPriorityOptions),
      loadEnumOptions('kb_board_column_status', setChannelStatusOptions),
      loadEnumOptions('milestone-subtask-status', setStatusOptions),
      loadEnumOptions('duedatefilter', setDueDateOptions),
      loadEnumOptions('kanbanduedatereminders', setReminderOptions)
    ]);
  }, [loadEnumOptions]);

  const fetchProjectBoardData = useCallback(async () => {
    if (!projectBoardId) return;

    setIsLoading(true);

    try {
      await loadProjectBoard(projectBoardId);
      await loadMilestones(projectBoardId, false);
    } catch (error) {
      console.error('Error fetching project board data:', error);
      boardToast('Failed to load the board. Please refresh and try again.', TOAST_TYPE.ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [projectBoardId, projectId]);

  const loadProjectBoard = async (projectBoardId) => {
    const projectBoard = await fetchProjectBoard(projectBoardId);

    if (!projectBoard) return;

    setUserPermission(projectBoard.permission);
    setTitle(projectBoard.name);
    setDefaultLabels(projectBoard.labels || []);

    if (setBoardTitle) {
      setBoardTitle(projectBoard.name);
    }

    setProjectBoardCreatedBy(projectBoard.createdBy);
    setTeams(formatTeamData(projectBoard.teams));
    setProgressBarList(projectBoard.channels);
    setIsProjectRelatedBoard(projectBoard.isProjectRelatedBoard);
  };
  const loadMilestones = async (projectBoardId, isArchived = false) => {
    if (!isLoading) {
      setIsMilestonesLoading(true);
    }
    const milestoneResponse = await fetchProjectMilestonesByBoard(projectBoardId, isArchived);
    // console.log(milestoneResponse, 'ProjrectmilestoneResponse');

    if (milestoneResponse?.length > 0) {
      setMilestones(milestoneResponse);
      setFilteredMilestones(milestoneResponse);
      setIsMilestonesLoading(false);
    } else {
      setMilestones([]);
      setIsMilestonesLoading(false);
    }
  };
  useEffect(() => {
    milestonesRef.current = milestones;
  }, [milestones]);

  useEffect(() => {
    if (!isMasterCalender) {
      if (projectDetail && isTemplateActive) {
        setTitle(projectDetail?.name || '');
        if (setBoardTitle) {
          setBoardTitle(projectDetail?.name);
        }
        setMilestones(projectDetail.milestones || []);
        setFilteredMilestones(projectDetail.milestones || []);
        // setUserPermission(projectDetail.permission);
        setProjectBoardCreatedBy(projectDetail.createdBy);
        // setTeams(formatTeamData(projectDetail.teams));
        setProgressBarList(projectDetail?.channels);
        // setIsProjectRelatedBoard(projectDetail?.isProjectRelatedBoard);
      } else {
        fetchProjectBoardData();
      }
    }
  }, [isTemplateActive, projectBoardId]);

  useEffect(() => {
    showArchivedCardsRef.current = showArchivedCards;
  }, [showArchivedCards]);

  useEffect(() => {
    let mastersocket = socket;
    if (isMasterCalender) {
      mastersocket = masterCalenderSocket;
    }

    if (!mastersocket) return undefined;

    const handleMilestones = (data) => {
      if (data.projectBoardId === projectBoardId) {
        console.log(showArchivedCards, 'showArchivedCards');

        let filteredMilestones = data.data?.filter((m) =>
          showArchivedCardsRef.current ? m?.isArchived : !m?.isArchived
        );
        setMilestones((prev) => mergeMilestoneList(prev, filteredMilestones));
        setFilteredMilestones((prev) => mergeMilestoneList(prev, filteredMilestones));
      }
    };
    const handleMilestoneAdded = (data) => {
      if (data.projectBoardId === projectBoardId || projectBoardIds?.includes(data.projectBoardId)) {
        // ✅ Append instead of replacing
        console.log(data, 'milestone-added');
        const newMilestone = Array.isArray(data.data) ? data.data : [data.data];

        setMilestones((prev) => sortMilestones([...prev, ...newMilestone]));
        setFilteredMilestones((prev) => sortMilestones([...prev, ...newMilestone]));
      }
    };
    const handleMilestoneCloned = (data) => {
      if (data.projectBoardId === projectBoardId || projectBoardIds?.includes(data.projectBoardId)) {
        // console.log(data, 'milestone-cloned');
        setMilestones((prev) => [...prev, data.data]);
        setFilteredMilestones((prev) => [...prev, data.data]);
      }
    };
    const handleMilestoneUpdated = (data) => {
      console.log(data, 'milestone-updated');
      console.log(showArchivedCardsRef.current, 'showArchivedCards');
      if (data.projectBoardId === projectBoardId || projectBoardIds?.includes(data.projectBoardId)) {
        setMilestones((prev) => {
          const updated = prev
            .map((m) => mergeMilestoneUpdate(m, data?.data))
            .filter((m) => (showArchivedCardsRef.current ? m?.isArchived : !m?.isArchived));

          return sortMilestones(updated);
        });

        setFilteredMilestones((prev) => {
          const updated = prev
            .map((m) => mergeMilestoneUpdate(m, data?.data))
            .filter((m) => (showArchivedCardsRef.current ? m?.isArchived : !m?.isArchived));

          return sortMilestones(updated);
        });
      }
    };

    const handleSubtaskDetail = (data) => {
      if (!data?.milestoneId) {
        return;
      }
      const updatedSubtask = data; // your incoming subtask

      setMilestones((prev) =>
        prev.map((milestone) => {
          if (milestone._id !== updatedSubtask?.milestoneId) {
            return milestone;
          }

          // 🔥 replace subtask inside milestone
          const updatedSubtasks = (milestone.subtask || []).map((st) =>
            st._id === updatedSubtask._id ? updatedSubtask : st
          );

          return {
            ...milestone,
            subtask: updatedSubtasks
          };
        })
      );

      setFilteredMilestones((prev) =>
        prev.map((milestone) => {
          if (milestone._id !== updatedSubtask.milestoneId) {
            return milestone;
          }

          const updatedSubtasks = (milestone.subtask || []).map((st) =>
            st._id === updatedSubtask._id ? updatedSubtask : st
          );

          return {
            ...milestone,
            subtask: updatedSubtasks
          };
        })
      );
    };

    const handleMilestoneDeleted = (data) => {
      // console.log(data.data, 'milestone-deleted');
      if (data.projectBoardId === projectBoardId || projectBoardIds?.includes(data.projectBoardId)) {
        setMilestones((prev) => prev.filter((m) => m._id !== data.data._id));
        setFilteredMilestones((prev) => prev.filter((m) => m._id !== data.data._id));
      }
    };
    const handleChannelCloned = (data) => {
      if (data.projectBoardId === projectBoardId || projectBoardIds?.includes(data.projectBoardId)) {
        const newMilestone = Array.isArray(data.milestones) ? data.milestones : [data.milestones];

        setMilestones((prev) => sortMilestones([...prev, ...newMilestone]));
        setFilteredMilestones((prev) => sortMilestones([...prev, ...newMilestone]));
      }
    };

    const handleProjectBoardLabels = (data) => {
      if (data.projectBoardId === projectBoardId) {
        // console.log(data, 'project-board-labels');
        setDefaultLabels(data.data);
      }
    };
    const handleChannels = (data) => {
      if (data.projectBoardId === projectBoardId) {
        setProgressBarList(data.data);
        console.log(data.data);
      }
    };

    mastersocket.on('milestones', handleMilestones);
    mastersocket.on('milestone-added', handleMilestoneAdded);
    mastersocket.on('milestone-cloned', handleMilestoneCloned);
    mastersocket.on('milestone-updated', handleMilestoneUpdated);
    mastersocket.on('subtask-detail', handleSubtaskDetail);
    mastersocket.on('milestone-deleted', handleMilestoneDeleted);
    mastersocket.on('channel-cloned', handleChannelCloned);
    mastersocket.on('project-board-labels', handleProjectBoardLabels);
    mastersocket.on('channels', handleChannels);

    return () => {
      mastersocket.off('milestones', handleMilestones);
      mastersocket.off('milestone-added', handleMilestoneAdded);
      mastersocket.off('milestone-cloned', handleMilestoneCloned);
      mastersocket.off('milestone-updated', handleMilestoneUpdated);
      mastersocket.off('subtask-detail', handleSubtaskDetail);
      mastersocket.off('milestone-deleted', handleMilestoneDeleted);
      mastersocket.off('channel-cloned', handleChannelCloned);
      mastersocket.off('project-board-labels', handleProjectBoardLabels);
      mastersocket.off('channels', handleChannels);
    };
  }, [isMasterCalender, masterCalenderSocket, projectBoardId, projectBoardIds, socket]);

  const handlePlusList = () => {
    setProgressBarList((prevState) => [...prevState, { name: '' }]);
  };

  const cancelEditChannelName = () => {
    const filteredValue = progressBarList.filter((value) => value.name);
    setProgressBarList(filteredValue);
  };

  const handleDrop = useCallback(
    (mileStoneData, dropItem) => {
      if (userPermission === 'View' || isTemplateActive || isArchivedActive) return;
      socket.emit('milestoneDragged', {
        projectBoardId,
        source: mileStoneData?._id,
        destination: dropItem?._id,
        destinationName: dropItem?.name
      });
    },
    [projectBoardId, socket, userPermission, isTemplateActive, isArchivedActive]
  );

  const handleSubmitChannel = async (id, value) => {
    if (userPermission === 'View' || isTemplateActive || isArchivedActive) return;
    try {
      if (id) {
        socket.emit('update-channel', { id, payload: { name: value }, projectBoardId });
      } else {
        socket.emit('add-channel', { name: value, projectBoardId });
      }
    } catch (err) {
      console.log('error', err);
    }
  };

  const handlecollapseChannel = async (id, isCollapsed) => {
    if (userPermission === 'View' || isTemplateActive || isArchivedActive) return;
    try {
      if (id) {
        socket.emit('update-channel', { id, payload: { isCollapsed }, projectBoardId: isTemplateActive ? activeTemplateId : projectBoardId });
      }
    } catch (err) {
      console.log('error', err);
    }
  };

  const handleCloneChannel = async (channelId, channelName) => {
    try {
      socket.emit('clone-channel', { channelId, projectBoardId, channelName });
    } catch (err) {
      console.log('error', err);
    }
  };

  const handleCloneMilestone = async (milestoneId) => {
    try {
      socket.emit('clone-milestone', { milestoneId, projectBoardId });
    } catch (err) {
      console.log('error', err);
    }
  };
  const handleArchiveMilestone = async (milestoneId, isArchived) => {
    try {
      socket.emit('update-milestone', { id: milestoneId, payload: { isArchived }, projectBoardId });
    } catch (err) {
      console.log('error', err);
    }
  };
  const handleArchiveChannelMilestones = async (channelId) => {
    try {
      socket.emit('archive-channel-milestones', { channelId, projectBoardId });
    } catch (err) {
      console.log('error', err);
    }
  };
  const handleDeleteChannel = async (id, channelName) => {
    socket.emit('delete-channel', { id, projectBoardId, channelName });
  };

  const handleDeleteMilestone = async () => {
    setDeleting(true);
    if (isSubtaskDeleting) {
      socket.emit('delete-subtask', { id: itemToDelete, milestoneId: parentMilestoneId }, projectBoardId);
    } else {
      socket.emit('delete-milestone', { id: itemToDelete, projectBoardId });
    }
    boardToast(`${isSubtaskDeleting ? 'Subtask' : 'Milestone'} deleted successfully.`, TOAST_TYPE.SUCCESS);
    setItemToDelete(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmItemDelete = async (id, parentMilestoneId = '', isSubtask = false) => {
    if (isSubtask) {
      setSubtaskDeleting(true);
      setParentMilestoneId(parentMilestoneId);
    } else {
      setSubtaskDeleting(false);
      setParentMilestoneId('');
    }
    setItemToDelete(id);
    setDeleteOpen(true);
  };

  // Save or update board title
  const handleSubmitTitle = async (value) => {
    socket.emit('update-project-board', {
      id: projectBoardId,
      payload: { name: value }
    });
    setBoards((prevState) =>
      prevState.map((state) => {
        if (state._id === projectBoardId) {
          return {
            ...state,
            name: value
          };
        } else return state;
      })
    );
    setTitle(value);
  };

  const handleSearchChange = (input) => {
    const search = typeof input === 'string' ? input : input.target.value;

    setSearchTerm(search);

    if (search) {
      const filteredData = milestones.filter(
        (value) =>
          (value.name && value.name.toLowerCase().includes(search.toLowerCase())) ||
          (value.description && value.description.toLowerCase().includes(search.toLowerCase()))
      );
      setFilteredMilestones(filteredData);
    } else {
      setFilteredMilestones(milestones);
    }

    setSearchValue(search);
  };

  const handleGoogleCalendarExport = async () => {
    if (isGoogleExporting) return;

    setGoogleExporting(true);

    try {
      const result = await exportEventsToGoogleCalendar({
        connected: Boolean(user?.googleCalendarConnected)
      });
      if (!user?.googleCalendarConnected) {
        await fetchUserInfo();
      }
      const exportedCount = result.created + result.updated;
      const syncedCount = exportedCount + (result.deleted || 0);

      if (syncedCount === 0 && result.unchanged > 0) {
        boardToast(`${result.unchanged} Google Calendar task(s) are already up to date.`, TOAST_TYPE.SUCCESS);
        return;
      }

      if (syncedCount === 0) {
        boardToast('No calendar tasks with due dates were available to export.', TOAST_TYPE.ERROR);
        return;
      }

      const skippedMessage = result.skipped ? ` ${result.skipped} task(s) without due dates were skipped.` : '';
      const unchangedMessage = result.unchanged ? ` ${result.unchanged} task(s) were already up to date.` : '';
      const deletedMessage = result.deleted ? ` ${result.deleted} outdated event(s) were removed.` : '';
      boardToast(
        `${exportedCount} task(s) exported to Google Calendar.${deletedMessage}${unchangedMessage}${skippedMessage}`,
        TOAST_TYPE.SUCCESS
      );
    } catch (error) {
      console.error('Google Calendar export failed:', error);
      boardToast(error.message || 'Unable to export tasks to Google Calendar.', TOAST_TYPE.ERROR);
    } finally {
      setGoogleExporting(false);
    }
  };

  const handleGoogleCalendarDisconnect = async () => {
    if (isGoogleDisconnecting) return;

    setGoogleDisconnecting(true);

    try {
      await disconnectGoogleCalendar();
      await fetchUserInfo();
      boardToast('Google Calendar sync disconnected.', TOAST_TYPE.SUCCESS);
    } catch (error) {
      console.error('Google Calendar disconnect failed:', error);
      boardToast(error.message || 'Unable to disconnect Google Calendar.', TOAST_TYPE.ERROR);
    } finally {
      setGoogleDisconnecting(false);
    }
  };

  const inviteTeam = async (data) => {
    // setIsLoading(true);
    setInviting(true);
    if (data._id) {
      const response = await Axios.put(`/project-board/add-team/${projectBoardId}`, {
        userId: data._id,
        projectId: projectId
      });
      if (response?.status && response?.status !== 200) {
        boardToast('Something went wrong', TOAST_TYPE.ERROR);
      } else {
        setTeams((prevState) => [...prevState, { ...data, permission: 'View' }]);
        boardToast('User invited successfully.', TOAST_TYPE.SUCCESS);
      }
    } else {
      const response = await Axios.put(`/project-board/invite-team/${projectBoardId}`, {
        email: data.email,
        projectId: projectId
      });
      if (response?.status && response?.status !== 200) {
        boardToast('Something went wrong', TOAST_TYPE.ERROR);
      } else {
        setTeams((prevState) => [...prevState, { ...data, permission: 'View' }]);
        boardToast('User invited successfully.', TOAST_TYPE.SUCCESS);
      }
    }
    setInviting(false);
    // setIsLoading(false);
  };

  const handleRemoveInvitee = async (data) => {
    const response = await Axios.put(`/project-board/remove-team/${projectBoardId}`, {
      userId: data._id,
      projectId: projectId
    });
    if (response) {
      const filterTeam = teams.filter((value) => value?._id !== data?._id);
      setTeams(filterTeam);
      boardToast('User removed successfully.', TOAST_TYPE.SUCCESS);
    }
  };

  const handleMilestoneClick = (value, isChecklist = false) => {
    if (isTemplateActive && projectDetail?.milestones?.length > 0) {
      const selectedMilestone = projectDetail.milestones.find((milestone) => milestone._id === value._id);
      setSelectedMilestone(selectedMilestone);
    }
    if (isMasterCalender && masterMilestones.length > 0) {
      const selectedMilestone = masterMilestones.find((milestone) => milestone._id === value.milestoneId);
      if (selectedMilestone) {
        setSelectedMilestone(selectedMilestone);
        // setUserPermission(
        //   selectedMilestone?.projectBoardId?.teams.find((x) => x.userId._id === user._id)?.permission?.name
        // );
        // setDefaultLabels(selectedMilestone?.projectBoardId?.labels || []);
        // setTeams(formatTeamData(selectedMilestone?.projectBoardId?.teams));
        // setProgressBarList(selectedMilestone?.projectBoardId?.channels);
      }
    }
    setChecklist(isChecklist);
    setSelectedSubtaskId(value._id || value?.milestoneId);
    setSubtaskOpen(false);
    setShowMilestoneSidebar(value._id || value?.milestoneId);
  };

  const handleSubtaskClick = (value) => {
    // console.log(value);
    setSubtaskOpen(true);
    if (isMasterCalender && masterMilestones.length > 0) {
      const selectedMilestone = masterMilestones.find((milestone) => milestone._id === value.milestoneId);
      if (selectedMilestone) {
        // setUserPermission(
        //   selectedMilestone?.projectBoardId?.teams.find((x) => x.userId._id === user._id)?.permission?.name
        // );
        // setDefaultLabels(selectedMilestone?.projectBoardId?.labels);
        // setTeams(formatTeamData(selectedMilestone?.projectBoardId?.teams));
        // setUserPermission(selectedMilestone?.projectBoardId?.teams[0]?.permission?.name);
        // setDefaultLabels(selectedMilestone?.projectBoardId?.labels);
        // setProgressBarList(selectedMilestone?.projectBoardId?.channels);
      }
    }
    setShowMilestoneSidebar(value.milestoneId);
    setSelectedSubtaskId(value._id || value?.subtaskId);
  };

  const handleArchiveRestoreBoard = async () => {
    try {
      const boardNode = boardRef.current;
      if (boardNode) {
        setSaving(true);
        const canvas = await html2canvas(boardNode, { useCORS: true });

        // Convert canvas to Blob instead of base64 string
        canvas.toBlob(async (blob) => {
          if (!blob) {
            console.error('Failed to capture blob from canvas');
            return;
          }

          const formData = new FormData();
          // formData.append('name', name);
          // formData.append('description', '');
          formData.append('boardImage', blob, 'screenshot.png'); // must pass blob + filename
          formData.append('action', (isArchivedActive || isArchivedParent) ? 'restore' : 'archive');

          // console.log('FormData content:');
          for (let [key, value] of formData.entries()) {
            console.log(key, value);
          }
          // Make the POST request
          let response = await APIServices.post(
            `/project-board/${(isArchivedActive || isArchivedParent)? archiveBoardId : projectBoardId}/archive`,
            formData,
            {
              'Content-Type': 'multipart/form-data'
            }
          );
          if (response?.message) {
            boardToast(response.message, TOAST_TYPE.SUCCESS);
            if (isArchivedActive) {
              fetchProjectBoards(false, archiveBoardId);
            } else {
              fetchProjectBoards();
            }
            // setSaveOpen(false);
            // setTemplateTitle('');
            if (isArchivedActive || isArchivedParent) {
              fetchArchivedBoards(1, parentBoardId);
            }
            setArchivedActive(false);
            setArchiveBoardId(null);
            setSaving(false);
            setShowConfirm(false);
          } else {
            setSaving(false);
          }
        }, 'image/png');
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const archiveConfirmContent = (
    <>
      <h2 className="mb-3">Are you sure?</h2>
      <h3>You want to {(isArchivedActive || isArchivedParent) ? 'restore' : 'close'} this board?</h3>
    </>
  );

  // if (isLoading) {
  //   return <div className="nodata-loader">{selectLoader(75)} </div>;
  // }

  const moveChannel = (fromIndex, toIndex, shouldEmit = false) => {
    if (userPermission === 'View' || isTemplateActive || isArchivedActive) return;
    let updatedList;

    setProgressBarList((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);

      // 🔹 Update sequence field locally so sorting stays correct
      updated.forEach((ch, idx) => {
        ch.sequence = idx + 1;
      });

      updatedList = updated;
      return updated;
    });

    if (shouldEmit && updatedList) {
      const payload = updatedList.map((ch) => ({
        id: ch._id,
        sequence: ch.sequence
      }));

      socket.emit('update-channel-sequence', { projectBoardId, channels: payload });
      console.log('Emitted final sequence:', payload);
    }
  };

  const hideAddList = userPermission === 'View' || progressBarList?.some((value) => !value?._id);

  return (
    <>
      <div className={`mx-0 pl-0 `}>
        <div className="mb-3">
          {/* {isLoading && <div className={style.text_editor_loader}>{selectLoader(60)} </div>} */}
          {/* {isTemplateActive ? (
            <div>
              <button className="share-btn">Use this templete</button>
            </div>
          ) : ( */}
          <ProjectBoardHeading
            title={title}
            handleSubmitTitle={handleSubmitTitle}
            teams={teams}
            searchValue={searchValue}
            handleSearchChange={handleSearchChange}
            projectBoardId={projectBoardId}
            inviteTeam={inviteTeam}
            handleRemoveInvitee={handleRemoveInvitee}
            hideEditButton={isTemplateActive ? true : userPermission === 'View'}
            projectBoardCreatedBy={projectBoardCreatedBy}
            viewMode={userPermission === 'View'}
            isInviting={isInviting}
            getBoardNode={() => boardRef.current}
            isTemplateActive={isTemplateActive}
            fetchTemplates={fetchTemplates}
            activeTemplateId={activeTemplateId}
            archiveBoardId={archiveBoardId}
            parentBoardId={parentBoardId}
            setBoards={setBoards}
            fetchProjectBoards={fetchProjectBoards}
            isGeneralPreview={isGeneralPreview}
            boardViewType={boardViewType}
            setBoardViewType={setBoardViewType}
            parentRef={parentRef}
            setCalenderViewType={setCalenderViewType}
            calenderViewType={calenderViewType}
            calendarTitle={calendarTitle}
            setShowConfirm={setShowConfirm}
            isArchivedActive={isArchivedActive}
            isSaving={isSaving}
            setSearchValue={setSearchValue}
            priorityOptions={priorityOptions}
            dueDateOptions={dueDateOptions}
            setMilestones={setMilestones}
            setFilteredMilestones={setFilteredMilestones}
            milestones={milestones}
            setCalendarDate={setCalendarDate}
            isMasterCalender={isMasterCalender}
            defaultLabels={defaultLabels}
            isProjectRelatedBoard={isProjectRelatedBoard}
            hideTabs={tabsAtTopLevel}
            channelStatusOptions={channelStatusOptions}
            setAddStatusOpen={setAddStatusOpen}
            reloadMilestones={loadMilestones}
            toggleBoardList={toggleBoardList}
            isBoardListCollapsed={isBoardListCollapsed}
            calendarEvents={calendarEvents}
            setFilteredCalendarEvents={setFilteredCalendarEvents}
            setshowArchivedCards={setshowArchivedCards}
            isArchivedParent={isArchivedParent}
            handleGoogleCalendarExport={handleGoogleCalendarExport}
            handleGoogleCalendarDisconnect={handleGoogleCalendarDisconnect}
            isGoogleExporting={isGoogleExporting}
            isGoogleDisconnecting={isGoogleDisconnecting}
            isGoogleCalendarConnected={Boolean(user?.googleCalendarConnected)}
            onTemplateRenamed={(name) => {
              setTitle(name);
              onTemplateRenamed(name);
            }}
            onTemplateDeleted={onTemplateDeleted}
            onUseTemplate={onUseTemplate}
          />
          {/* )} */}
        </div>
      </div>
      <div className={style.milestones_container}>
        {(isMilestonesLoading || isLoading) && (
          <div className={style.milestones_loader_overlay}>{selectLoader(60)}</div>
        )}

        {/* ✅ OVERVIEW */}
        {/* <div style={{ display: boardViewType === 'overview' && isProjectRelatedBoard ? 'block' : 'none' }}>
          <UpdatedOverview embedded={true} />
        </div> */}

        {/* ✅ BOARD */}
        <div style={{ display: boardViewType === 'board' && !isMasterCalender ? 'block' : 'none' }}>
          <div>
            <div className={style.kanban_draggable_wrapper} ref={boardRef}>
              {[...progressBarList]
                ?.sort((a, b) => a.sequence - b.sequence)
                ?.map((value, index) => (
                  <ChannelWrapper
                    key={value._id || index}
                    name={value.name}
                    id={value?._id}
                    handleMilestoneClick={handleMilestoneClick}
                    handleDrop={handleDrop}
                    milestones={filteredMilestones}
                    handleSubmitChannel={(title) => handleSubmitChannel(value._id, title)}
                    handleDeleteChannel={() => handleDeleteChannel(value._id, value.name)}
                    projectBoardId={projectBoardId}
                    cancelEditChannelName={cancelEditChannelName}
                    viewMode={isTemplateActive || isArchivedActive ? true : userPermission === 'View'}
                    progressBarList={progressBarList}
                    isProjectRelatedBoard={isProjectRelatedBoard}
                    handleDeleteMilestone={handleConfirmItemDelete}
                    handleDeleteSubtask={handleConfirmItemDelete}
                    handleSubtaskClick={handleSubtaskClick}
                    openSubtasks={openSubtasks}
                    setOpenSubtasks={setOpenSubtasks}
                    members={teams}
                    priorityOptions={priorityOptions}
                    moveChannel={moveChannel}
                    index={index}
                    statusOptions={statusOptions}
                    handleCloneMilestone={handleCloneMilestone}
                    defaultLabels={defaultLabels}
                    reminderOptions={reminderOptions}
                    handlecollapseChannel={handlecollapseChannel}
                    defaultCollapsed={value?.isCollapsed}
                    channelStatus={value.status}
                    setAddStatusOpen={setAddStatusOpen}
                    handleArchiveMilestone={handleArchiveMilestone}
                    setInstructionsOpen={() => {
                      setSelectedChannel(value);
                      setInstructionsOpen(true);
                    }}
                    channelDetails={value}
                    handleCloneChannel={handleCloneChannel}
                    handleArchiveChannelMilestones={() => handleArchiveChannelMilestones(value._id)}
                    showArchivedCards={showArchivedCards}
                    isTemplateActive={isTemplateActive}
                  />
                ))}

              {!hideAddList && (
                <div className={style.task_wrapper_heading_new} onClick={handlePlusList}>
                  <button className="btn btn-light icon-btn">
                    <img src={PLUS_ICON} alt="search-icon" />
                  </button>
                  <div>Add another list</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ✅ CALENDAR */}
        <div style={{ display: boardViewType === 'calendar' ? 'block' : 'none' }}>
          <TaskCalendar
            ref={parentRef}
            calenderViewType={calenderViewType}
            onDateChange={(title, date) => {
              setCalendarTitle(title);
              setCalendarDate(date);
            }}
            projectBoardId={projectBoardId}
            teams={teams}
            handleMilestoneClick={handleMilestoneClick}
            handleSubtaskClick={handleSubtaskClick}
            calendarDate={calendarDate}
            searchTerm={searchTerm}
            setCalenderViewType={setCalenderViewType}
            isMasterCalender={isMasterCalender}
            teamMembers={teams}
            priorityOptions={priorityOptions}
            reminderOptions={reminderOptions}
            channelOptions={channelOptions}
            isProjectRelatedBoard={isProjectRelatedBoard}
            calendarEvents={filteredCalendarEvents}
            readOnly={!isMasterCalender && userPermission === 'View'}
          />
        </div>
      </div>
      <DeleteModal
        show={deleteOpen}
        onHide={() => {
          setDeleteOpen(false);
          setItemToDelete(null); // Clear the member to delete on cancel
        }}
        handleDelete={handleDeleteMilestone} // Pass the deletion logic here
        itemName={isSubtaskDeleting ? 'subtask' : 'task'}
        loading={isDeleting}
      />
      {showMilestoneSidebar && (
        <MilestoneModal
          channels={progressBarList}
          projectTitle={title}
          milestoneId={showMilestoneSidebar} //can be id of subtask/milestone
          show={!!showMilestoneSidebar}
          onHide={() => setShowMilestoneSidebar(null)}
          milestoneMembers={teams}
          disabledMode={(isTemplateActive || isArchivedParent) ? true : userPermission === 'View'}
          projectBoardId={projectBoardId}
          isSubtaskOpen={isSubtaskOpen}
          selectedSubtaskId={selectedSubtaskId}
          showChecklist={showChecklist}
          priorityOptions={priorityOptions}
          isTemplateActive={isTemplateActive}
          projectDetail={projectDetail}
          statusOptions={statusOptions}
          isProjectRelatedBoard={isProjectRelatedBoard}
          defaultMilestoneLabels={defaultLabels}
          isMasterCalender={isMasterCalender}
          user={user}
          selectedMilestone={selectedMilestone}
          masterCalenderSocket={masterCalenderSocket}
          reminderOptions={reminderOptions}
          toggleModal={toggleModal}
          formatTeamData={formatTeamData}
        />
      )}
      <ConfirmModal
        show={showConfirm}
        onHide={() => {
          setShowConfirm(false);
        }}
        confirmationText={archiveConfirmContent}
        handleAction={handleArchiveRestoreBoard}
        closeText="Yes"
        loading={isSaving}
        bgClassname={'whitebgmodal'}
      />
      <AddStatusModal
        channelslist={progressBarList}
        channelStatusOptions={channelStatusOptions}
        show={addStatusOpen}
        projectBoardId={projectBoardId}
        onHide={() => {
          setAddStatusOpen(false);
        }}
      />
      <InstructionsModal
        show={instructionsOpen}
        channelDetails={selectedChannel}
        onHide={() => {
          setInstructionsOpen(false);
          setSelectedChannel('');
        }}
        projectBoardId={projectBoardId}
        closeText="Yes"
      />
    </>
  );
}
